import { NgSelectExtensionDirective } from '@shared/directives/ng-select-extension.directive';
import { merge, of, Subject, Subscription } from 'rxjs';
import { priceToPenny } from '@shared/helpers/price-to-penny.helper';
import { TranslateService } from '@ngx-translate/core';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { DiscountComponent } from '@shared/modules/tasks/components/task-components/discount/discount.component';
import { TaskSidenavService } from '@shared/modules/task-sidenav/services/task-sidenav.service';
import { Config } from '@shared/configs/config';
import { StorageService } from '@core/services/storage.service';
import { PricePipe } from '@shared/pipes/price.pipe';
import { TASK_SIDENAV_FORM } from '@shared/modules/task-sidenav/const/task-sidenav-form';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';
import { TaskType } from '@shared/enums/task-type.enum';
import { AttachmentType } from '@shared/components/text-attachments-input/enums/text-attachment-type.enum';
import { AttachmentService } from '@shared/services/attachment.service';
import { SnackBarService } from '@core/services/snackbar.service';
import { getFileType } from '@shared/helpers/basic-file-type.helper';
import { TaskSelectionV2ChangeComponent } from '@shared/modules/tasks/components/task-selection-change-v2/task-selection-change-v2.component';
import { InputV2Component } from '@shared/modules/forms-v2/components/input-v2/input-v2.component';
import { ProjectChangesSettlementStatus } from '@modules/projects/shared/enums/project-change-settlement-status.enum';
import { GbxsoftErrorTypes } from '@form/src/lib/controllers/gbxsoft-form-control-error.controller';
import { MatDialog } from '@angular/material/dialog';
import { ImageAttachmentModalComponent } from '@shared/components/image-attachment-modal/image-attachment-modal.component';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import { ListTaskService } from '@shared/modules/list/services/list-task.service';
import { Validators } from '@angular/forms';
import { TaskInfluence } from '@shared/enums/task-influence.enum';
import { TaskTypeList } from '@shared/consts/task-type.list';
import { ChangeType } from '../../../../enums/change-type.enum';

type InputEditorType = 'content' | 'internalNote';

@Component({
  selector: 'task-basic-edit-data',
  templateUrl: './task-basic-edit-data.component.html',
  styleUrls: ['./task-basic-edit-data.component.scss']
})
export class TaskBasicEditDataComponent implements OnInit, AfterViewInit, OnDestroy {
  TaskType = TaskType;
  private readonly processTaskTypes = new Set(['documentation', 'finance', 'control', 'message']);

  @ViewChild('discountTempl') discountTempl: DiscountComponent;
  @ViewChild('financeTempl') financeTempl: TaskSelectionV2ChangeComponent;
  @ViewChild('daysTempl') daysTempl: TaskSelectionV2ChangeComponent;
  @ViewChild('taskTitleInput') taskTitleInput: InputV2Component;
  @ViewChild(NgSelectExtensionDirective) protocolList: NgSelectExtensionDirective;
  @ViewChild('editor') editor;
  @ViewChild('internalNoteEditor') internalNoteEditor;

  @Output('onSubmit') onSubmit: EventEmitter<any> = new EventEmitter<any>();
  @Output() attachmentAdded: EventEmitter<any> = new EventEmitter<any>();

  private readonly refreshGroups$ = new Subject<void>();

  private projectIdCtrl = this.service.form.get(TASK_SIDENAV_FORM.projectId);

  projectSub: Subscription;
  currentFinanceState: TaskInfluence;
  currentDateState: TaskInfluence;
  clipboardFileName: string;
  isProjectRequired: boolean;

  canProjectAdd: boolean = true;

  taskList = TaskTypeList.map((i) => {
    i.name = this.t.instant(i.name);
    return i;
  });

  sub: Subscription;
  typeChange: Subscription = new Subscription();
  priceSub: Subscription = new Subscription();

  modules(inputType: InputEditorType) {
    return {
      toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image', 'video']
      ],
      imageHandler: {
        upload: (file) => {
          return new Promise((resolve, reject) => {
            this.zone.run(() => {
              this.dialog
                .open(ImageAttachmentModalComponent, { width: Config.CONFIRM_MODAL_WIDTH })
                .afterClosed()
                .subscribe((data) => {
                  if ((data?.asAttachment && !data?.asImage) || !data)
                    this.clearImageFromQuillAtIndex('content');

                  if (data?.asAttachment) this.attachmentAdded.emit(file);
                  if (data?.asImage) {
                    return this.uploadFile(file, 'content', { resolve, reject });
                  }
                });
            });
          });
        },
        accepts: ['*']
      } as any,
      imageDropAndPaste: {
        handler: this.uploadImage.bind(this)
      }
    };
  }

  formats: string[] = [
    'background',
    'bold',
    'color',
    'font',
    'code',
    'italic',
    'link',
    'size',
    'strike',
    'script',
    'underline',
    'blockquote',
    'header',
    'indent',
    'list',
    'align',
    'direction',
    'code-block',
    'formula',
    'image',
    'video'
  ];

  settlementsItems = [
    { name: 'Projects.ProjectSettlement.null', value: ProjectChangesSettlementStatus.CHANGE_SETTLEMENT_NONE },
    {
      name: 'Projects.ProjectSettlement.unsettled',
      value: ProjectChangesSettlementStatus.CHANGE_SETTLEMENT_NOT_SETTLED
    },
    {
      name: 'Projects.ProjectSettlement.settled',
      value: ProjectChangesSettlementStatus.CHANGE_SETTLEMENT_SETTLED
    }
  ].map((i) => {
    i.name = this.t.instant(i.name);
    return i;
  });

  get taskType(): TaskType {
    return this.service.form.get(this.service.TASK_SIDENAV_FORM.type).value;
  }

  get currentTaskType(): string {
    return (
      this.service?.task?.type ||
      this.service?.form?.get(this.service.TASK_SIDENAV_FORM.type)?.value ||
      this.service?.activityTemplateTaskType ||
      this.service?.TaskConfigType ||
      TaskType.TYPE_TASK
    );
  }

  get isProcessTaskContext(): boolean {
    return (
      !!this.service?.isProcessTaskContext ||
      this.service?.groupType === 'activity-template' ||
      !!this.service?.activityTemplateContext ||
      this.processTaskTypes.has(this.currentTaskType)
    );
  }

  get titlePlaceholderKey(): string {
    return this.isProcessTaskContext
      ? 'Projects.processTaskPlaceholders.title'
      : 'ProjectContacts.List.name';
  }

  get descriptionPlaceholderKey(): string {
    if (!this.isProcessTaskContext) {
      return `Projects.enterDescription-${this.service.FormType}`;
    }

    const type = this.processTaskTypes.has(this.currentTaskType) ? this.currentTaskType : 'task';
    return `Projects.processTaskPlaceholders.description.${type}`;
  }

  get protocolURL() {
    const control = this.service.form.get(this.service.TASK_SIDENAV_FORM.projectId);
    return Config.API + '/protocol' + (control?.value ? `?filters[project.id][eq]=${control?.value}` : '');
  }

  errorMessages(name: string) {
    const messages = Config.validationMessages;
    const control = this.service.form.get(name);

    if (control?.errors?.maxlength?.requiredLength) {
      messages[GbxsoftErrorTypes.maxLength] = this.t.instant('FormErrors.maxLength', {
        number: control.errors?.maxlength?.requiredLength
      });
    }

    if (control?.errors?.minlength?.requiredLength) {
      messages[GbxsoftErrorTypes.minLength] = this.t.instant('FormErrors.minLength', {
        number: control.errors?.minlength?.requiredLength
      });
    }

    return Object.keys(messages)
      .filter((key) => Object.keys(control.errors || {}).includes(key))
      .reduce((cur, key) => {
        return Object.assign(cur, { [key]: messages[key] });
      }, {});
  }

  get SelectPriceValue() {
    if (!this.service?.task?.isPriceEstimated) {
      return this.service.financial[0];
    }

    if (this.service?.task?.price > 0) {
      return this.service.financial[1];
    } else if (this.service?.task?.price < 0) {
      return this.service.financial[2];
    } else if (this.service?.task?.price === 0) {
      return this.service.financial[3];
    } else {
      return this.service.financial[0];
    }
  }

  get SelectDaysValue() {
    const time = Number(this.service?.task?.additionalTime?.toString());
    if (!this.service?.task?.isAdditionalTimeEstimated) {
      return this.service.days[0];
    }

    if (time > 0) {
      return this.service.days[1];
    } else if (time < 0) {
      return this.service.days[2];
    } else if (time === 0) {
      return this.service.days[3];
    } else {
      return this.service.days[0];
    }
  }

  get InputDaysValue() {
    if (!this.service?.task?.isAdditionalTimeEstimated) return null;

    return Math.abs(!!this.service.task?.additionalTime ? this.service.task?.additionalTime : 0);
  }

  get InputPriceValue() {
    if (!this.service?.task?.isPriceEstimated) return null;

    return this.service?.task?.price !== undefined
      ? this.pricePipe.insertValueToInput(
          Math.abs(!!this.service?.task?.price ? this.service?.task?.price : 0),
          true
        )
      : null;
  }

  get isFinanceDisabled() {
    return (
      this.currentFinanceState === TaskInfluence.NO_CHANGES ||
      this.currentFinanceState === TaskInfluence.NO_ESTIMATION
    );
  }

  get isDateDisabled() {
    return (
      this.currentDateState === TaskInfluence.NO_CHANGES ||
      this.currentDateState === TaskInfluence.NO_ESTIMATION
    );
  }

  taskGroups$ = (this.listTaskService.isMyWorkTaskLists
    ? this.projectIdCtrl.valueChanges.pipe(
        startWith(this.projectIdCtrl.value),
        distinctUntilChanged(),
        tap((pid) => {
          if (!this.projectIdCtrl.pristine && this.service.groupType !== 'project') {
            this.resetProjectControl();
          }
          this.listTaskService.projectId = pid;
        }),
        switchMap((pid) => {
          const groupType = pid || this.service.groupType === 'project' ? 'project' : 'without_project';
          return this.taskService.getWorkspaceTaskGroupByType(groupType);
        })
      )
    : merge(
        this.refreshGroups$,
        this.projectIdCtrl.valueChanges.pipe(
          startWith(this.projectIdCtrl.value),
          distinctUntilChanged(),
          tap((pid) => (this.service.projectID = pid))
        )
      ).pipe(
        switchMap(() => {
          const formTypeValue = this.service.form.get(this.service.TASK_SIDENAV_FORM.type)!.value;
          const currentProjectId = this.projectIdCtrl.value;

          if (formTypeValue === 'change') return of([]);
          return this.taskService.getTaskGroupByType(formTypeValue, currentProjectId);
        })
      )
  ).pipe(
    tap((groups) => this.setDefaultTaskGroup(groups ?? [])),
    map((groups) =>
      ((groups ?? []) as []).map(({ id, name }) => ({
        id,
        name: name === 'general' ? this.t.instant('ProjectTasks.TaskGroup.general') : name
      }))
    )
  );

  protected readonly changeTypes = Object.values(ChangeType).map((key) => ({
    id: key as ChangeType,
    name: this.t.instant(`Projects.changeType.${key}`)
  })) as Array<{ id: ChangeType; name: string }>;

  constructor(
    private t: TranslateService,
    private s: SnackBarService,
    public service: TaskSidenavService,
    public storage: StorageService,
    private pricePipe: PricePipe,
    private attachmentService: AttachmentService,
    private dialog: MatDialog,
    private zone: NgZone,
    private taskService: TaskService,
    public listTaskService: ListTaskService
  ) {}

  ngOnInit() {
    this.subscribeProjectUpdate();

    if (this.listTaskService.isMyWorkTaskLists) {
      this.resetValidatorsForMyWork();

      if (this.service.groupType === 'project') {
        this.service.form.get(TASK_SIDENAV_FORM.projectId).setValidators([Validators.required]);
        this.isProjectRequired = true;
      }

      if (this.service.groupType === 'without_project' && !this.service?.task?.parentId) {
        this.service.form.get(TASK_SIDENAV_FORM.assignedTo).setValidators([Validators.required]);
      }
    }
  }

  ngAfterViewInit() {
    this.checkProjectPermissions();
    this.setFocusToTaskTitle();
    this.initialChangeTemplate();
    const sub = this.service.form
      .get(TASK_SIDENAV_FORM.type)
      .valueChanges.pipe(tap(() => this.initialChangeTemplate()))
      .subscribe();
    this.initEditorCustomMatcher();
    this.typeChange.add(sub);
  }

  clearImageFromQuillAtIndex(inputType: string): void {
    const quill = inputType === 'content' ? this.editor.quillEditor : this.internalNoteEditor.quillEditor;
    const selection = quill.getSelection();
    const index = selection ? selection.index : quill.getLength() - 1;

    const [blot] = quill.getLeaf(index);
    if (!blot) return;

    const node = blot.domNode as HTMLElement;

    if (node.tagName === 'IMG' && node.getAttribute('src')?.startsWith('data:')) {
      quill.deleteText(index - 1, 1, 'user');
    }

    const progressBarEl = quill.container.parentElement?.querySelector('.quill-progress-wrapper');
    progressBarEl?.remove();
  }

  resetProjectControl(): void {
    if (this.listTaskService.isMyWorkTaskLists) {
      this.service.form.get(this.service.TASK_SIDENAV_FORM.groupId).reset(null);
    }
  }

  resetValidatorsForMyWork(): void {
    this.service.form.get(TASK_SIDENAV_FORM.projectId).setValidators([]);
    this.service.form.get(TASK_SIDENAV_FORM.assignedTo).setValidators([]);
  }

  initEditorCustomMatcher(): void {
    document.querySelector('.quill-custom-matcher').addEventListener('paste', (e: ClipboardEvent) => {
      const clipboardData = e.clipboardData || (window as any).clipboardData;
      const isImage = clipboardData.types.length && clipboardData.types.includes('Files');
      if (!isImage) return;
      const file = clipboardData.files[0];
      this.clipboardFileName = file.name;
    });
  }

  initialChangeTemplate() {
    this.setFinanceAndDays();
    this.financeAndDaysChange();
    this.service.registerChangeTemplates({
      financeTemplate: this.financeTempl,
      daysTemplate: this.daysTempl
    });
  }

  imageHandler(imageDataUrl, type, imageData) {
    const file = imageData.toFile();
    this.service.attachmentsListRef.onFilesChange([file]);
  }

  setTypeActive(item) {
    if (this.isProcessTaskContext) return;
    this.service.form.get(this.service.TASK_SIDENAV_FORM.type).setValue(item.type);
    this.refreshGroups$.next();
    this.service.setTaskTypeFromSave();
    this.service.updateTaskType(item.type);
  }

  setFocusToTaskTitle() {
    this.taskTitleInput.inputv2.nativeElement.focus();
  }

  subscribeProjectUpdate() {
    this.projectSub = this.service.form
      .get(TASK_SIDENAV_FORM.projectId)
      .valueChanges.pipe(
        distinctUntilChanged(),
        tap((value) => this.service.form.get(TASK_SIDENAV_FORM.protocolId).setValue(null))
      )
      .pipe(
        debounceTime(0),
        tap(() => this.protocolList?.getItems())
      )
      .subscribe();
  }

  private setDefaultTaskGroup(groups: Array<{ id: number; name: string }>): void {
    if (!this.service.isCreation || this.isProcessTaskContext) return;

    const groupIdControl = this.service.form.get(this.service.TASK_SIDENAV_FORM.groupId);
    if (groupIdControl?.value) return;

    const defaultGroup =
      groups.find((group) => String(group?.name || '').toLowerCase() === 'general') || groups[0];

    if (!defaultGroup?.id) return;

    this.service.setGroupID(defaultGroup.id, false);
  }

  checkProjectPermissions() {
    this.canProjectAdd = true;
  }

  isActiveTaskTypeTab(item: { name: string; type: TaskType }) {
    const val: TaskType = this.service.form.get(this.service.TASK_SIDENAV_FORM.type).value;
    return val === item.type || (val === TaskType.TYPE_COMPANY_TASK && item.type === TaskType.TYPE_TASK);
  }

  financeAndDaysChange() {
    if (!this.service.isConfigChange && this.service.form?.value?.type !== TaskType.TYPE_CHANGE) {
      return;
    }

    this.priceSub?.unsubscribe();

    this.priceSub = new Subscription();
    const daysControl = this.daysTempl.form.get('input');
    const subDays = this.daysTempl.form.valueChanges.subscribe((value) => {
      if (daysControl.valid) {
        this.service.form
          .get(this.service.TASK_SIDENAV_FORM.additionalTime)
          .setValue(this.getInfluenceCount('daysTempl'));

        this.service.form
          .get(this.service.TASK_SIDENAV_FORM.isAdditionalTimeEstimated)
          .setValue(this['daysTempl'].form.value?.select?.id === TaskInfluence.NO_ESTIMATION ? 0 : 1);
      }
    });

    const financeControl = this.financeTempl.form.get('input');
    const subFinance = this.financeTempl.form.valueChanges.subscribe((value) => {
      if (financeControl.valid) {
        this.service.form
          .get(this.service.TASK_SIDENAV_FORM.price)
          .setValue(priceToPenny(this.getInfluenceCount('financeTempl')));
        this.service.form
          .get(this.service.TASK_SIDENAV_FORM.isPriceEstimated)
          .setValue(this['financeTempl'].form.value?.select?.id === TaskInfluence.NO_ESTIMATION ? 0 : 1);
      }
    });

    this.priceSub.add(subDays);
    this.priceSub.add(subFinance);
  }

  onEnter(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      this.onSubmit.emit();
    }
  }

  setFinanceAndDays() {
    if (!this.service.isConfigChange) {
      return;
    }
    this.currentFinanceState = this.SelectPriceValue.id;
    this.currentDateState = this.SelectDaysValue.id;

    this.financeTempl.form.setValue({
      select: this.SelectPriceValue,
      input: this.InputPriceValue
    });

    this.daysTempl.form.setValue({
      select: this.SelectDaysValue,
      input: this.InputDaysValue
    });
  }

  influenceChangeEvent($event, templateName: string, stateName: string) {
    switch ($event.id) {
      case TaskInfluence.NO_CHANGES:
        this[templateName].form.get('input').setValue(templateName === 'financeTempl' ? '0.00' : '0');
        break;
      case TaskInfluence.NO_ESTIMATION:
        this[templateName].form.get('input').setValue(null);
        break;
      default:
        if (this[templateName].form.get('input').value === 0) {
          this[templateName].form.get('input').setValue('');
        }
        setTimeout(() => this[templateName].input?.inputElement?.focus(), 50);
        break;
    }
    this[stateName] = $event.id;
  }

  getInfluenceCount(templateName: string) {
    const select = this[templateName]?.form.get('select').value;
    const input = this[templateName]?.form.get('input').value;
    let value: any = Math.abs(input);
    switch (select.id) {
      case TaskInfluence.PLUS:
        break;
      case TaskInfluence.MINUS:
        value = value * -1;
        break;
      case TaskInfluence.NO_CHANGES:
        value = 0;
      case TaskInfluence.NO_ESTIMATION:
        value = '';
        break;
    }
    return value;
  }

  responseTransform(resp) {
    return [];
  }

  isControlInvalid(name: string) {
    return this.service.form?.get(name)?.touched && this.service.form?.get(name)?.invalid;
  }

  uploadImage(imageDataUrl, type, imageData): void {
    const quill = this.editor.quillEditor;
    const selection = quill.getSelection();
    const insertIndex = selection ? selection.index : quill.getLength();

    this.clearImageFromQuillAtIndex('content');

    this.zone.run(() => {
      this.dialog
        .open(ImageAttachmentModalComponent, { width: Config.CONFIRM_MODAL_WIDTH })
        .afterClosed()
        .subscribe((data) => {
          const file = imageData.toFile(this.clipboardFileName || imageData.name);

          if (data?.asAttachment) this.attachmentAdded.emit(file);

          if (data?.asImage) {
            this.uploadFile(file, 'content', {
              resolve: (url) => {
                quill.insertEmbed(insertIndex - 1, 'image', url, 'user');
                setTimeout(() => quill.setSelection(insertIndex, 0));
              }
            });
          }

          this.clipboardFileName = '';
        });
    });
  }

  uploadFile(file: File, inputType: InputEditorType, opt: { resolve?: any; reject?: any } = {}) {
    const { resolve, reject } = opt;
    if (getFileType(file) === AttachmentType.FILE) {
      this.s.error(this.t.instant('ProjectCreator.imagesAllowed'));
      reject('Unsupported');
      return;
    }
    const quill = inputType === 'content' ? this.editor.quillEditor : this.internalNoteEditor.quillEditor;
    let index = (quill.getSelection() || {}).index;
    if (index === undefined || index < 0) index = quill.getLength();
    let data = {};

    this.attachmentService
      .uploadAttachment(
        {
          objectType: 'wysiwyg-content',
          files: [file]
        },
        data
      )
      .subscribe({
        next: (data) => {
          let url = `${Config.PRIVATE_ATTACHMENT}${data[0].file}`;
          if (resolve) {
            resolve(url);
          } else {
            this.service.form.get(this.service.TASK_SIDENAV_FORM[inputType]).setValue(quill.root.innerHTML);
          }
        },
        error: () => {
          reject ? reject('Unsupported') : null;
        }
      });
  }

  ngOnDestroy() {
    this.projectSub?.unsubscribe();
    this.priceSub?.unsubscribe();
    this.typeChange?.unsubscribe();
  }
}
